import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Copy, BarChart3, Activity, FileSearch } from "lucide-react";
import SEO from "@/components/SEO";
import {
  adminMe,
  setAdminToken,
  getAdminToken,
  adminAnalyticsFunnel,
  adminAnalyticsDimensions,
  adminAnalyticsEvents,
} from "@/lib/api";
import { useSessionRenewal } from "@/hooks/useSessionRenewal";

const RANGE_OPTIONS = [7, 14, 30, 60, 90];

export default function AdminAnalytics() {
  const nav = useNavigate();
  const [days, setDays] = useState(30);
  const [funnel, setFunnel] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getAdminToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    adminMe()
      .then((res) => {
        if (res?.expires_at) setAdminToken(getAdminToken(), res.expires_at);
      })
      .catch(() => {
        setAdminToken("");
        nav("/admin/login", { replace: true });
      });
  }, [nav]);

  useSessionRenewal({
    onExpired: () => {
      setAdminToken("");
      nav("/admin/login", { replace: true });
    },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, c, e] = await Promise.all([
        adminAnalyticsFunnel(days),
        adminAnalyticsDimensions(),
        adminAnalyticsEvents({ limit: 50 }),
      ]);
      setFunnel(f);
      setCatalog(c);
      setRecent(e.events || []);
    } catch (err) {
      console.warn("analytics load failed", err);
      toast.error("Could not load analytics.");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const copy = async (txt, label) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(`${label} copied.`);
    } catch {
      window.prompt(`Copy ${label}:`, txt);
    }
  };

  return (
    <main data-testid="admin-analytics-page" className="bg-white min-h-screen">
      <SEO title="Analytics" path="/admin/analytics" noIndex />

      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="ash-eyebrow">Ashlan Admin</span>
            <h1 className="text-2xl md:text-3xl font-bold text-[#080808]">Analytics</h1>
          </div>
          <Link
            to="/admin/blog"
            data-testid="back-to-blog"
            className="text-sm text-[#3A7A8C] hover:underline inline-flex items-center gap-1.5"
          >
            <ArrowLeft size={14} /> Blog console
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Range picker */}
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="range-picker">
          <span className="text-xs uppercase tracking-[0.18em] font-semibold text-[#6B7280] mr-2">
            Range
          </span>
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setDays(r)}
              data-testid={`range-${r}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-semibold border ${
                days === r
                  ? "bg-[#3A7A8C] text-white border-[#3A7A8C]"
                  : "bg-white text-[#3C4048] border-[#e5e7eb] hover:bg-[#F5F5F5]"
              }`}
            >
              Last {r}d
            </button>
          ))}
        </div>

        {loading || !funnel ? (
          <div className="flex items-center justify-center py-12 text-[#5A5F6A]">
            <Loader2 className="animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            {/* D4 — Conversion funnel */}
            <div className="mb-12" data-testid="funnel-section">
              <div className="flex items-center gap-3 mb-6">
                <BarChart3 className="text-[#3A7A8C]" size={18} />
                <h2 className="text-xl md:text-2xl font-bold text-[#080808]">
                  Conversion funnel · {funnel.distinct_visitors} distinct visitors
                </h2>
              </div>
              <div className="border border-[#e5e7eb] divide-y divide-[#e5e7eb]" data-testid="funnel-steps">
                {funnel.funnel.map((step) => (
                  <div
                    key={step.label}
                    data-testid={`funnel-step-${step.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="p-5 flex flex-wrap items-center gap-4"
                  >
                    <div className="min-w-[160px] text-sm font-semibold text-[#080808]">
                      {step.label}
                    </div>
                    <div className="flex-1 h-3 bg-[#F5F5F5] relative overflow-hidden min-w-[180px]">
                      <div
                        className="absolute inset-y-0 left-0 bg-[#5FA8B4] transition-all"
                        style={{ width: `${step.rate}%` }}
                      />
                    </div>
                    <div className="text-sm tabular-nums font-semibold text-[#080808] min-w-[80px] text-right">
                      {step.count.toLocaleString()}
                    </div>
                    <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#3A7A8C] min-w-[60px] text-right">
                      {step.rate}%
                    </div>
                  </div>
                ))}
              </div>

              {/* Form completion summary (D6) */}
              <div className="mt-6 grid sm:grid-cols-4 gap-4" data-testid="form-summary">
                <Tile label="Form starts" value={funnel.forms.starts} />
                <Tile label="Form submits" value={funnel.forms.submits} accent />
                <Tile label="Form abandons" value={funnel.forms.abandons} />
                <Tile label="Completion rate" value={`${funnel.forms.completion_rate}%`} accent />
              </div>
            </div>

            {/* Daily breakdown */}
            {funnel.by_day.length > 0 && (
              <div className="mb-12" data-testid="daily-breakdown">
                <div className="flex items-center gap-3 mb-6">
                  <Activity className="text-[#3A7A8C]" size={18} />
                  <h2 className="text-xl md:text-2xl font-bold text-[#080808]">
                    Daily event volume
                  </h2>
                </div>
                <div className="border border-[#e5e7eb] overflow-x-auto">
                  <table className="w-full text-sm" data-testid="daily-table">
                    <thead className="bg-[#F5F5F5]">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] font-semibold text-[#6B7280]">
                          Date
                        </th>
                        {catalog?.funnel_events?.map((ev) => (
                          <th
                            key={ev}
                            className="px-3 py-3 text-right text-xs uppercase tracking-[0.15em] font-semibold text-[#6B7280] whitespace-nowrap"
                          >
                            {ev}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {funnel.by_day.slice(-14).reverse().map((row) => (
                        <tr key={row.date} className="border-t border-[#e5e7eb]">
                          <td className="px-4 py-2.5 font-semibold text-[#080808] whitespace-nowrap">
                            {row.date}
                          </td>
                          {catalog?.funnel_events?.map((ev) => (
                            <td
                              key={ev}
                              className="px-3 py-2.5 text-right tabular-nums text-[#3C4048]"
                            >
                              {row[ev] || 0}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* D3 — Dimensions catalog */}
            <div className="mb-12" data-testid="dimensions-section">
              <div className="flex items-center gap-3 mb-6">
                <FileSearch className="text-[#3A7A8C]" size={18} />
                <h2 className="text-xl md:text-2xl font-bold text-[#080808]">
                  GA4 Setup — events &amp; custom dimensions
                </h2>
              </div>
              <div className="border-l-4 border-[#5FA8B4] bg-[#F5F5F5] p-5 mb-6">
                <p className="text-sm text-[#3C4048] leading-relaxed">
                  Mirror these into <strong>GA4 → Admin → Custom Definitions</strong> so
                  you can report on them. Scope determines where each dimension shows up
                  (user / session / event). Measurement ID detected:{" "}
                  <code className="bg-white px-1.5 py-0.5 border border-[#e5e7eb] text-[#080808]">
                    {catalog?.ga_measurement_id || "not configured"}
                  </code>
                  .
                </p>
              </div>

              <div className="border border-[#e5e7eb] overflow-x-auto mb-6">
                <table className="w-full text-sm" data-testid="dimensions-table">
                  <thead className="bg-[#F5F5F5]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] font-semibold text-[#6B7280]">
                        Dimension
                      </th>
                      <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] font-semibold text-[#6B7280]">
                        Scope
                      </th>
                      <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] font-semibold text-[#6B7280]">
                        Description
                      </th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {catalog?.custom_dimensions?.map((d) => (
                      <tr key={d.key} className="border-t border-[#e5e7eb]" data-testid={`dim-${d.key}`}>
                        <td className="px-4 py-3 font-mono text-xs text-[#080808] font-semibold whitespace-nowrap">
                          {d.key}
                        </td>
                        <td className="px-4 py-3 text-xs uppercase tracking-[0.12em] font-semibold text-[#3A7A8C]">
                          {d.scope}
                        </td>
                        <td className="px-4 py-3 text-[#3C4048]">{d.description}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => copy(d.key, "Dimension name")}
                            data-testid={`copy-dim-${d.key}`}
                            className="inline-flex items-center gap-1.5 text-xs text-[#3A7A8C] hover:underline"
                          >
                            <Copy size={11} /> Copy
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#6B7280] mb-3">
                Events we currently fire
              </div>
              <div className="flex flex-wrap gap-2" data-testid="events-list">
                {catalog?.funnel_events?.map((ev) => (
                  <button
                    type="button"
                    key={ev}
                    onClick={() => copy(ev, "Event name")}
                    data-testid={`copy-event-${ev}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono font-semibold text-[#080808] bg-white border border-[#e5e7eb] hover:border-[#3A7A8C]"
                  >
                    {ev} <Copy size={10} className="opacity-60" />
                  </button>
                ))}
              </div>
            </div>

            {/* Recent events stream */}
            <div data-testid="recent-events-section">
              <div className="flex items-center gap-3 mb-6">
                <Activity className="text-[#3A7A8C]" size={18} />
                <h2 className="text-xl md:text-2xl font-bold text-[#080808]">
                  Recent events
                </h2>
              </div>
              {recent.length === 0 ? (
                <div className="border border-dashed border-[#e5e7eb] p-8 text-center text-[#6B7280]">
                  No events recorded yet. Visit the public site or submit a form to populate.
                </div>
              ) : (
                <div className="border border-[#e5e7eb] divide-y divide-[#e5e7eb]" data-testid="recent-events-list">
                  {recent.slice(0, 25).map((e) => (
                    <div
                      key={e.id}
                      className="p-3 flex flex-wrap items-center gap-3 text-xs font-mono"
                      data-testid={`event-row-${e.id}`}
                    >
                      <span className="text-[#6B7280]">
                        {(e.created_at || "").replace("T", " ").slice(0, 19)}
                      </span>
                      <span className="font-semibold text-[#3A7A8C] uppercase tracking-[0.1em]">
                        {e.event_name}
                      </span>
                      <span className="text-[#3C4048]">{e.page_path}</span>
                      {e.params && Object.keys(e.params).length > 0 && (
                        <span className="text-[#6B7280] truncate max-w-md">
                          {JSON.stringify(e.params)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}

function Tile({ label, value, accent }) {
  return (
    <div
      className={`border p-4 ${
        accent ? "border-[#3A7A8C] bg-[#F0F7F9]" : "border-[#e5e7eb] bg-white"
      }`}
    >
      <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#6B7280]">
        {label}
      </div>
      <div className="mt-2 text-2xl font-bold text-[#080808] tabular-nums">{value}</div>
    </div>
  );
}
